import { useEffect, useState } from 'react';

/**
 * Bildene i en annonse: ett stort, de andre som miniatyrer under.
 *
 * Et lokale velges på bildene. Derfor er det første bildet stort med én gang,
 * og de andre ett klikk unna – ikke en stripe med like små bilder man må
 * forstørre ett og ett. Klikk på det store åpner det i full størrelse, og der
 * blar man videre med pilene eller tastaturet.
 *
 * Ett bilde vises uten piler og miniatyrer: en bla-knapp mellom ett bilde og
 * seg selv er bare støy.
 */
export function Bildegalleri({ bilder, navn }: { bilder: string[]; navn: string }) {
  const [aktiv, setAktiv] = useState(0);
  const [full, setFull] = useState(false);
  const antall = bilder.length;

  // Bildene kan byttes ut under føttene på oss (portalen sletter ett), og da
  // skal ikke pekeren stå utenfor lista.
  useEffect(() => {
    if (aktiv >= antall) setAktiv(Math.max(0, antall - 1));
  }, [aktiv, antall]);

  const forrige = () => setAktiv((i) => (i - 1 + antall) % antall);
  const neste = () => setAktiv((i) => (i + 1) % antall);

  // Piltastene virker bare i fullskjerm: på selve sida ville de stjålet
  // rullingen fra alle som leser teksten under.
  useEffect(() => {
    if (!full) return;
    const paaTast = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFull(false);
      else if (e.key === 'ArrowLeft' && antall > 1) forrige();
      else if (e.key === 'ArrowRight' && antall > 1) neste();
    };
    document.addEventListener('keydown', paaTast);
    return () => document.removeEventListener('keydown', paaTast);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full, antall]);

  if (antall === 0) return null;
  const gjeldende = bilder[Math.min(aktiv, antall - 1)]!;
  const src = (id: string) => `/api/leverandorbilder/${id}`;

  return (
    <div className="galleri">
      <div className="galleri-hoved">
        {antall > 1 ? (
          <button
            type="button"
            className="galleri-pil venstre"
            onClick={forrige}
            aria-label="Forrige bilde"
          >
            ‹
          </button>
        ) : null}
        <button
          type="button"
          className="galleri-bilde"
          onClick={() => setFull(true)}
          aria-label="Vis bildet i full størrelse"
        >
          <img src={src(gjeldende)} alt={`${navn}, bilde ${aktiv + 1} av ${antall}`} />
        </button>
        {antall > 1 ? (
          <button type="button" className="galleri-pil hoyre" onClick={neste} aria-label="Neste bilde">
            ›
          </button>
        ) : null}
        {antall > 1 ? (
          <span className="galleri-teller" aria-hidden="true">
            {aktiv + 1} / {antall}
          </span>
        ) : null}
      </div>

      {antall > 1 ? (
        <div className="galleri-miniatyrer">
          {bilder.map((b, i) => (
            <button
              key={b}
              type="button"
              className={`miniatyr${i === aktiv ? ' aktiv' : ''}`}
              onClick={() => setAktiv(i)}
              aria-label={`Vis bilde ${i + 1}`}
              aria-pressed={i === aktiv}
            >
              <img src={src(b)} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      ) : null}

      {full ? (
        <div
          className="bildeoverlegg"
          role="dialog"
          aria-modal="true"
          aria-label="Bilde i full størrelse"
          tabIndex={-1}
          onClick={() => setFull(false)}
          ref={(el) => el?.focus()}
        >
          {antall > 1 ? (
            <button
              type="button"
              className="overlegg-pil venstre"
              onClick={(e) => {
                e.stopPropagation();
                forrige();
              }}
              aria-label="Forrige bilde"
            >
              ‹
            </button>
          ) : null}
          <img
            src={src(gjeldende)}
            alt={`${navn}, bilde ${aktiv + 1} av ${antall}`}
            onClick={(e) => e.stopPropagation()}
          />
          {antall > 1 ? (
            <button
              type="button"
              className="overlegg-pil hoyre"
              onClick={(e) => {
                e.stopPropagation();
                neste();
              }}
              aria-label="Neste bilde"
            >
              ›
            </button>
          ) : null}
          <button
            type="button"
            className="overlegg-lukk"
            onClick={() => setFull(false)}
            aria-label="Lukk"
          >
            ×
          </button>
        </div>
      ) : null}
    </div>
  );
}
